<template>
  <div class="vehicle-job-card bg-secondary text-white rounded-lg shadow-xl w-96 flex flex-col" style="max-height: 80vh;">
    <!-- Header: Vehicle + Tester -->
    <div class="flex items-center justify-between p-4 pb-3 flex-shrink-0">
      <div class="flex items-center">
        <div
          :class="[
            'w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm',
            statusBgClass
          ]"
        >
          {{ initials }}
        </div>
        <div class="ml-3">
          <div class="font-bold text-sm">{{ vehicle.assignedUserName || 'Unassigned' }}</div>
          <div class="text-xs text-gray-400">{{ vehicle.name || vehicle.id }}</div>
        </div>
      </div>
      <span @click="$emit('close')" class="text-gray-400 hover:text-white cursor-pointer text-lg">&times;</span>
    </div>

    <!-- Day Summary Bar -->
    <div class="px-4 pb-3 flex-shrink-0">
      <div class="bg-gray-800 rounded-lg p-3">
        <div class="flex items-center justify-between text-xs">
          <div class="flex items-center space-x-3">
            <span class="text-gray-300">
              <i class="fa-solid fa-briefcase text-gray-500 mr-1"></i>
              {{ dayStats ? dayStats.totalJobs : allJobs.length }} jobs
            </span>
            <span class="text-green-400">
              <i class="fa-solid fa-check text-green-500 mr-1"></i>
              {{ dayStats ? dayStats.jobsCompleted : completedCount }} done
            </span>
            <span class="text-gray-300">
              <i class="fa-solid fa-road text-gray-500 mr-1"></i>
              {{ dayStats ? dayStats.distanceKm : 0 }} km
            </span>
            <span class="text-gray-300">
              <i class="fa-solid fa-clock text-gray-500 mr-1"></i>
              {{ formattedDriveTime }}
            </span>
          </div>
        </div>
        <!-- Concerns badges -->
        <div v-if="concerns.length > 0" class="flex flex-wrap gap-1.5 mt-2">
          <span
            v-for="(c, i) in concerns"
            :key="i"
            :class="[
              'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
              c.type === 'danger' ? 'bg-red-900 text-red-300' :
              c.type === 'warning' ? 'bg-yellow-900 text-yellow-300' :
              'bg-blue-900 text-blue-300'
            ]"
          >
            <i :class="[c.icon, 'mr-1']" style="font-size:9px"></i>
            {{ c.label }}
          </span>
        </div>
      </div>
    </div>

    <!-- Status pill row -->
    <div class="px-4 pb-2 flex-shrink-0">
      <span
        :class="[
          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
          statusPillClass
        ]"
      >
        <span :class="['w-1.5 h-1.5 rounded-full mr-1.5', statusDotClass]"></span>
        {{ statusLabel }}
      </span>
      <span v-if="vehicle.speed != null" class="text-xs text-gray-500 ml-2">
        {{ Math.round(vehicle.speed || 0) }} km/h
      </span>
      <span v-if="dayPathLoading" class="text-xs text-gray-500 ml-2">
        <i class="fa-solid fa-spinner fa-spin mr-1"></i>Loading path...
      </span>
    </div>

    <!-- Day Timeline (scrollable) -->
    <div class="flex-grow overflow-y-auto px-4 pb-2 min-h-0">
      <div v-if="sortedJobs.length > 0" class="relative">
        <!-- Vertical timeline line -->
        <div class="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-700"></div>

        <div
          v-for="(job, i) in sortedJobs"
          :key="job.id || i"
          :class="[
            'relative pl-8 py-2',
            isCurrentJob(job) ? 'border-l-2 border-blue-500 ml-2.5' : 'ml-3'
          ]"
        >
          <!-- Timeline dot -->
          <div
            :class="[
              'absolute top-3 w-2.5 h-2.5 rounded-full border-2',
              timelineDotClass(job)
            ]"
            :style="{ left: isCurrentJob(job) ? '0px' : '2px' }"
          ></div>

          <!-- Job content -->
          <div
            :class="['rounded p-2 cursor-pointer transition-all', isCurrentJob(job) ? 'bg-gray-800' : 'hover:bg-gray-800 hover:bg-opacity-50', focusedJobId === job.id ? 'ring-1 ring-primary' : '']"
            @click.stop="$emit('focus-job', job)"
          >
            <div class="flex items-center justify-between">
              <!-- Time: click to edit (disabled in read-only mode) -->
              <span
                v-if="editingTimeJobId !== job.id"
                @click.stop="!readOnlyMode && startEditTime(job)"
                :class="['text-xs', readOnlyMode ? 'text-gray-500' : 'text-gray-500 hover:text-primary cursor-pointer']"
                :title="readOnlyMode ? '' : 'Click to change time'"
              >
                <i class="fa-regular fa-clock mr-0.5" style="font-size:9px"></i>
                {{ formatJobTime(job) }}
              </span>
              <input
                v-else
                type="time"
                :value="editTimeValue"
                @change="onTimeChange($event, job)"
                @blur="cancelEditTime"
                @keyup.escape="cancelEditTime"
                ref="timeInput"
                class="text-xs bg-gray-700 text-white border border-gray-600 rounded px-1.5 py-0.5 w-24 outline-none focus:border-primary"
              />

              <!-- Status: click to toggle dropdown -->
              <span v-if="editingStatusJobId !== job.id">
                <span
                  @click.stop="startEditStatus(job)"
                  :class="[
                    'text-xs font-medium px-1.5 py-0.5 rounded cursor-pointer hover:ring-1 hover:ring-gray-500',
                    jobStatusClass(job)
                  ]"
                  title="Click to change status"
                >
                  {{ jobStatusIcon(job) }} {{ jobStatusLabel(job) }}
                </span>
              </span>
              <select
                v-else
                :value="job.status"
                @change="onStatusChange($event, job)"
                @blur="editingStatusJobId = null"
                ref="statusSelect"
                class="text-xs bg-gray-700 text-white border border-gray-600 rounded px-1 py-0.5 outline-none focus:border-primary cursor-pointer"
              >
                <option value="FIRM">FIRM</option>
                <option value="ON-CALL">ON-CALL</option>
                <option value="COMMITTED">COMMITTED</option>
                <option value="COMPLETED">COMPLETED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </div>

            <div class="text-sm font-bold mt-0.5 truncate">
              {{ job.company || job.projectName || job.projectId || 'Job' }}
            </div>
            <div class="text-xs text-gray-400 truncate">{{ formatAddress(job) }}</div>

            <!-- Extra info for current job -->
            <div v-if="isCurrentJob(job) && eta" class="flex items-center mt-1.5">
              <span :class="['text-xs font-bold', isLate ? 'text-red-400' : 'text-green-400']">
                ETA: {{ eta }} min
              </span>
              <span v-if="isLate && lateBy" class="ml-2 text-xs text-red-400 font-bold">{{ lateBy }} min late</span>
              <span v-else-if="!isLate" class="ml-2 text-xs text-green-400 font-bold">ON TIME</span>
            </div>

            <!-- Mix/meters/duration info -->
            <div v-if="customFieldsEnabled" class="text-xs text-gray-500 mt-0.5">
              <span v-for="(f, n) in testerFields(job)" :key="f.key">
                <span v-if="n"> | </span>{{ f.label }}: {{ f.value }}
              </span>
              <span v-if="job.duration">
                <span v-if="testerFields(job).length"> | </span><i class="fa-regular fa-clock mr-0.5"></i>{{ job.duration }} min
              </span>
            </div>
            <div v-else-if="job.mixCode || job.meters || job.duration" class="text-xs text-gray-500 mt-0.5">
              <span v-if="job.mixCode">Mix: {{ job.mixCode }}</span>
              <span v-if="job.mixCode && job.meters"> | </span>
              <span v-if="job.meters">{{ job.meters }}m</span>
              <span v-if="(job.mixCode || job.meters) && job.duration"> | </span>
              <span v-if="job.duration"><i class="fa-regular fa-clock mr-0.5"></i>{{ job.duration }} min</span>
            </div>

            <!-- Read-only mode: Notes editor -->
            <div v-if="readOnlyMode" class="mt-1.5">
              <div v-if="editingNotesJobId === job.id" @click.stop>
                <textarea v-model="editNotesValue" rows="2" class="w-full text-xs bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 outline-none focus:border-primary" placeholder="Add notes..."></textarea>
                <div class="flex items-center space-x-2 mt-1">
                  <button @click.stop="saveJobNotes(job)" class="text-xs text-primary hover:text-white">Save</button>
                  <button @click.stop="editingNotesJobId = null" class="text-xs text-gray-500 hover:text-white">Cancel</button>
                </div>
              </div>
              <div v-else class="flex items-center space-x-3">
                <button @click.stop="startEditNotes(job)" class="text-xs text-gray-500 hover:text-primary">
                  <i class="fa-solid fa-note-sticky mr-1"></i>{{ job.notes ? 'Edit Notes' : 'Add Notes' }}
                </button>
                <button v-if="allTeamLeaders.length > 0" @click.stop="openInviteModal(job)" class="text-xs text-gray-500 hover:text-yellow-400">
                  <i class="fa-solid fa-user-plus mr-1"></i>Invite Leader
                </button>
                <button v-if="!job.contractor && teamMembers.length > 0" @click.stop="assigningTesterJobId = assigningTesterJobId === job.id ? null : job.id" class="text-xs text-gray-500 hover:text-green-400">
                  <i class="fa-solid fa-user mr-1"></i>Assign Tester
                </button>
              </div>
              <!-- Notes display -->
              <div v-if="job.notes && editingNotesJobId !== job.id" class="text-xs text-gray-400 mt-1 bg-gray-800 rounded px-2 py-1">{{ job.notes }}</div>
              <!-- Assign tester dropdown -->
              <div v-if="assigningTesterJobId === job.id" class="mt-1.5" @click.stop>
                <select @change="onAssignTester($event, job)" class="text-xs bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 w-full outline-none">
                  <option value="">Select tester...</option>
                  <option v-for="m in teamMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
                </select>
              </div>
            </div>

            <!-- Action row: Edit full job + saving indicator -->
            <div v-if="!readOnlyMode" class="flex items-center justify-between mt-1.5">
              <a
                :href="'/jobs?jobId=' + job.id"
                target="_blank"
                class="text-xs text-gray-500 hover:text-primary transition-colors"
                title="Edit full job in scheduler"
                @click.stop
              >
                <i class="fa-solid fa-pen-to-square mr-1"></i>Edit Job
              </a>
              <span v-if="savingJobId === job.id" class="text-xs text-gray-500">
                <i class="fa-solid fa-spinner fa-spin mr-1"></i>Saving...
              </span>
              <span v-if="savedJobId === job.id" class="text-xs text-green-400">
                <i class="fa-solid fa-check mr-1"></i>Saved
              </span>
            </div>
            <!-- Saving/saved indicator for read-only mode -->
            <div v-if="readOnlyMode" class="mt-1">
              <span v-if="savingJobId === job.id" class="text-xs text-gray-500">
                <i class="fa-solid fa-spinner fa-spin mr-1"></i>Saving...
              </span>
              <span v-if="savedJobId === job.id" class="text-xs text-green-400">
                <i class="fa-solid fa-check mr-1"></i>Saved
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-xs text-gray-500 py-3 text-center">No jobs assigned today</div>

      <!-- Invite Team Leader Modal Overlay -->
      <div v-if="showInviteModal && inviteJobId" class="fixed inset-0 z-50 flex items-center justify-center" @click.self="showInviteModal = false">
        <div class="bg-gray-800 border border-gray-600 rounded-lg shadow-xl p-4 w-72">
          <h4 class="text-sm font-bold text-white mb-3">Invite Team Leader</h4>
          <div v-for="tl in availableTeamLeadersForInvite" :key="tl.id"
            @click="inviteTeamLeader(tl)"
            class="flex items-center px-3 py-2 rounded hover:bg-gray-700 cursor-pointer text-sm text-gray-300 hover:text-white">
            <i class="fa-solid fa-star text-yellow-500 mr-2" style="font-size:10px"></i>
            {{ tl.name }}
            <span v-if="tl.region" class="text-xs text-gray-500 ml-1">({{ tl.region }})</span>
          </div>
          <div v-if="availableTeamLeadersForInvite.length === 0" class="text-xs text-gray-500 py-2 text-center">No available team leaders</div>
          <button @click="showInviteModal = false" class="mt-3 text-xs text-gray-500 hover:text-white w-full text-center">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Bottom section (fixed) -->
    <div class="flex-shrink-0 p-4 pt-2 border-t border-gray-700">
      <!-- Job stage milestones -->
      <div v-if="currentJob && jobStage !== 'idle'" class="flex space-x-1 mb-3">
        <div
          v-for="(stage, i) in stages"
          :key="i"
          :class="[
            'h-1.5 flex-grow rounded-full',
            stage.active ? stage.color : 'bg-gray-600'
          ]"
          :title="stage.label"
        ></div>
      </div>

      <!-- Driver Score -->
      <driver-score-card
        v-if="driverScore"
        :score="driverScore.score"
        :metrics="driverScore.metrics"
      />

      <!-- Last updated -->
      <div class="text-xs text-gray-600 mt-3">
        Updated {{ formatTime(vehicle.lastUpdate) }}
      </div>
    </div>
  </div>
</template>

<script>
import DriverScoreCard from './driverScoreCard.vue';
import { setFire, postFire, createTeamLeaderInvite } from '../../firestore/utils';
import { loadDefinitions, resolveFieldValues } from '../../services/customFields';
import { getAuth } from 'firebase/auth';

function formatAddressFromJob(job) {
  if (!job) return 'No address';
  if (job.formattedAddress) return job.formattedAddress;
  if (job.address && typeof job.address === 'object') {
    const a = job.address;
    const parts = [a.street_number, a.route, a.locality, a.administrative_area_level_1, a.postal_code].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }
  if (typeof job.address === 'string') return job.address;
  return 'No address';
}

function toJsDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  return new Date(val);
}

export default {
  components: { DriverScoreCard },
  props: {
    vehicle: { type: Object, required: true },
    currentJob: { type: Object, default: null },
    nextJob: { type: Object, default: null },
    eta: { type: Number, default: null },
    isLate: { type: Boolean, default: false },
    lateBy: { type: Number, default: 0 },
    driverScore: { type: Object, default: null },
    jobStage: { type: String, default: 'idle' },
    allJobs: { type: Array, default: () => [] },
    dayStats: { type: Object, default: null },
    futureJobs: { type: Array, default: () => [] },
    dayPathLoading: { type: Boolean, default: false },
    focusedJobId: { type: String, default: null },
    readOnlyMode: { type: Boolean, default: false },
    teamMembers: { type: Array, default: () => [] },
    allTeamLeaders: { type: Array, default: () => [] }
  },
  data() {
    return {
      editingTimeJobId: null,
      editTimeValue: '',
      editingStatusJobId: null,
      savingJobId: null,
      savedJobId: null,
      savedTimer: null,
      editingNotesJobId: null,
      editNotesValue: '',
      showInviteModal: false,
      inviteJobId: null,
      assigningTesterJobId: null,
      customFieldDefs: null,
    };
  },
  created() {
    loadDefinitions().then((defs) => { this.customFieldDefs = defs; });
  },
  computed: {
    customFieldsEnabled() {
      return !!(this.customFieldDefs && this.customFieldDefs.enabled);
    },
    initials() {
      const name = this.vehicle.assignedUserName || '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },
    completedCount() {
      return this.allJobs.filter(j => j.status === 'COMPLETED').length;
    },
    formattedDriveTime() {
      const min = this.dayStats ? this.dayStats.driveTimeMin : 0;
      if (min < 60) return `${min}m`;
      const h = Math.floor(min / 60);
      const m = min % 60;
      return `${h}h ${m}m`;
    },
    sortedJobs() {
      if (!this.allJobs || this.allJobs.length === 0) return [];
      return [...this.allJobs].sort((a, b) => {
        const da = toJsDate(a.date);
        const db = toJsDate(b.date);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da - db;
      });
    },
    concerns() {
      const list = [];
      const lateCount = this.allJobs.filter(j => {
        if (j === this.currentJob && this.isLate) return true;
        return false;
      }).length;
      if (lateCount > 0) {
        list.push({ label: `${lateCount} late arrival${lateCount > 1 ? 's' : ''}`, type: 'danger', icon: 'fa-solid fa-triangle-exclamation' });
      }
      if (this.driverScore && this.driverScore.score < 70) {
        list.push({ label: 'Low driver score', type: 'warning', icon: 'fa-solid fa-star' });
      }
      if (this.driverScore && this.driverScore.metrics && this.driverScore.metrics.overspeedEvents > 0) {
        list.push({ label: `${this.driverScore.metrics.overspeedEvents} overspeed`, type: 'warning', icon: 'fa-solid fa-gauge-high' });
      }
      const now = new Date();
      const hour = now.getHours();
      const remaining = this.dayStats ? this.dayStats.jobsRemaining : 0;
      if (hour >= 14 && remaining > 3) {
        list.push({ label: 'Overloaded', type: 'warning', icon: 'fa-solid fa-weight-hanging' });
      }
      return list;
    },
    vehicleStatus() {
      if (!this.vehicle.online) return 'offline';
      if ((this.vehicle.speed || 0) < 2) return 'idle';
      return 'moving';
    },
    statusLabel() {
      if (this.jobStage === 'en_route') return 'En Route';
      if (this.jobStage === 'arrived') return 'Arrived';
      if (this.jobStage === 'testing') return 'Testing';
      if (this.jobStage === 'completed') return 'Completed';
      if (this.vehicleStatus === 'offline') return 'Offline';
      if (this.vehicleStatus === 'idle') return 'Idle';
      return 'Moving';
    },
    statusBgClass() {
      const map = {
        en_route: 'bg-blue-500',
        arrived: 'bg-yellow-500',
        testing: 'bg-purple-500',
        completed: 'bg-green-500'
      };
      if (map[this.jobStage]) return map[this.jobStage];
      if (this.vehicleStatus === 'offline') return 'bg-gray-500';
      if (this.vehicleStatus === 'idle') return 'bg-gray-400';
      return 'bg-blue-500';
    },
    statusPillClass() {
      const map = {
        en_route: 'bg-blue-900 text-blue-300',
        arrived: 'bg-yellow-900 text-yellow-300',
        testing: 'bg-purple-900 text-purple-300',
        completed: 'bg-green-900 text-green-300'
      };
      if (map[this.jobStage]) return map[this.jobStage];
      if (this.vehicleStatus === 'offline') return 'bg-gray-700 text-gray-400';
      return 'bg-gray-700 text-gray-300';
    },
    statusDotClass() {
      const map = {
        en_route: 'bg-blue-400',
        arrived: 'bg-yellow-400',
        testing: 'bg-purple-400',
        completed: 'bg-green-400'
      };
      if (map[this.jobStage]) return map[this.jobStage];
      if (this.vehicleStatus === 'offline') return 'bg-gray-500';
      return 'bg-gray-400';
    },
    stages() {
      const stageOrder = ['en_route', 'arrived', 'testing', 'completed'];
      const currentIdx = stageOrder.indexOf(this.jobStage);
      return [
        { label: 'En Route', color: 'bg-blue-500', active: currentIdx >= 0 },
        { label: 'Arrived', color: 'bg-yellow-500', active: currentIdx >= 1 },
        { label: 'Testing', color: 'bg-purple-500', active: currentIdx >= 2 },
        { label: 'Completed', color: 'bg-green-500', active: currentIdx >= 3 }
      ];
    },
    availableTeamLeadersForInvite() {
      if (!this.inviteJobId) return [];
      const job = this.sortedJobs.find(j => j.id === this.inviteJobId);
      if (!job) return this.allTeamLeaders;
      const assignedIds = job.teamLeaderIds || [];
      return this.allTeamLeaders.filter(tl => !assignedIds.includes(tl.id));
    }
  },
  methods: {
    testerFields(job) {
      if (!this.customFieldsEnabled) return [];
      return resolveFieldValues(this.customFieldDefs, job, 'tester');
    },
    formatAddress(job) {
      return formatAddressFromJob(job);
    },
    formatTime(timestamp) {
      if (!timestamp) return 'Never';
      const d = toJsDate(timestamp);
      if (!d) return 'Never';
      const now = new Date();
      const diffMs = now - d;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return diffMin + ' min ago';
      return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    },
    formatJobTime(job) {
      if (!job || !job.date) return '--:--';
      const d = toJsDate(job.date);
      if (!d) return '--:--';
      return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
    },
    isCurrentJob(job) {
      if (!this.currentJob || !job) return false;
      return job.id === this.currentJob.id;
    },
    timelineDotClass(job) {
      if (job.status === 'COMPLETED') return 'bg-green-500 border-green-500';
      if (job.status === 'CANCELLED') return 'bg-red-500 border-red-500';
      if (this.isCurrentJob(job)) return 'bg-blue-500 border-blue-500 ring-2 ring-blue-400 ring-opacity-50';
      return 'bg-gray-600 border-gray-500';
    },
    jobStatusClass(job) {
      if (job.status === 'COMPLETED') return 'bg-green-900 text-green-300';
      if (job.status === 'CANCELLED') return 'bg-red-900 text-red-300';
      if (this.isCurrentJob(job)) return 'bg-blue-900 text-blue-300';
      return 'bg-gray-700 text-gray-400';
    },
    jobStatusIcon(job) {
      if (job.status === 'COMPLETED') return '✓';
      if (job.status === 'CANCELLED') return '✗';
      if (this.isCurrentJob(job)) return '→';
      return '○';
    },
    jobStatusLabel(job) {
      if (job.status === 'COMPLETED') return 'Done';
      if (job.status === 'CANCELLED') return 'Cancelled';
      if (this.isCurrentJob(job)) return 'Current';
      return 'Pending';
    },

    // ── Inline Time Editing ──
    startEditTime(job) {
      if (!job.id) return;
      const d = toJsDate(job.date);
      if (d) {
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        this.editTimeValue = `${hh}:${mm}`;
      } else {
        this.editTimeValue = '';
      }
      this.editingTimeJobId = job.id;
      this.$nextTick(() => {
        if (this.$refs.timeInput) {
          const el = Array.isArray(this.$refs.timeInput) ? this.$refs.timeInput[0] : this.$refs.timeInput;
          if (el) el.focus();
        }
      });
    },
    cancelEditTime() {
      this.editingTimeJobId = null;
      this.editTimeValue = '';
    },
    async onTimeChange(event, job) {
      const timeStr = event.target.value;
      if (!timeStr || !job.id) {
        this.cancelEditTime();
        return;
      }
      const [hours, minutes] = timeStr.split(':').map(Number);
      const existingDate = toJsDate(job.date);
      const newDate = existingDate ? new Date(existingDate) : new Date();
      newDate.setHours(hours, minutes, 0, 0);

      this.editingTimeJobId = null;
      await this.saveJobTime(job, newDate);
    },
    async saveJobTime(job, newDate) {
      this.savingJobId = job.id;
      try {
        await setFire({
          collection: 'jobs',
          doc: job.id,
          body: { date: newDate }
        });
        // Audit trail
        const user = getAuth().currentUser;
        if (user) {
          await postFire({
            collection: 'customerPosts',
            body: {
              postedBy: user.uid,
              type: 'timeUpdated',
              body: `Job time updated to ${newDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}, for ${job.projectName || job.company || ''} at ${formatAddressFromJob(job)}.`,
              projectId: job.projectId,
              customerId: job.customerId,
              created: new Date()
            }
          });
        }
        // Update local data
        job.date = newDate;
        this.showSaved(job.id);
        this.$emit('job-updated', job.id);
      } catch (e) {
        console.error('Error updating job time:', e);
      } finally {
        this.savingJobId = null;
      }
    },

    // ── Inline Status Editing ──
    startEditStatus(job) {
      if (!job.id) return;
      this.editingStatusJobId = job.id;
      this.$nextTick(() => {
        if (this.$refs.statusSelect) {
          const el = Array.isArray(this.$refs.statusSelect) ? this.$refs.statusSelect[0] : this.$refs.statusSelect;
          if (el) el.focus();
        }
      });
    },
    async onStatusChange(event, job) {
      const newStatus = event.target.value;
      this.editingStatusJobId = null;
      if (newStatus === job.status) return;
      await this.saveJobStatus(job, newStatus);
    },
    async saveJobStatus(job, newStatus) {
      this.savingJobId = job.id;
      try {
        await setFire({
          collection: 'jobs',
          doc: job.id,
          body: { status: newStatus }
        });
        const user = getAuth().currentUser;
        if (user) {
          await postFire({
            collection: 'customerPosts',
            body: {
              postedBy: user.uid,
              type: 'statusUpdated',
              body: `Job status updated to ${newStatus}, for ${job.projectName || job.company || ''} at ${formatAddressFromJob(job)}.`,
              projectId: job.projectId,
              customerId: job.customerId,
              created: new Date()
            }
          });
        }
        job.status = newStatus;
        this.showSaved(job.id);
        this.$emit('job-updated', job.id);
      } catch (e) {
        console.error('Error updating job status:', e);
      } finally {
        this.savingJobId = null;
      }
    },

    showSaved(jobId) {
      if (this.savedTimer) clearTimeout(this.savedTimer);
      this.savedJobId = jobId;
      this.savedTimer = setTimeout(() => {
        this.savedJobId = null;
      }, 2000);
    },

    // ── Read-Only Mode: Notes ──
    startEditNotes(job) {
      this.editingNotesJobId = job.id;
      this.editNotesValue = job.notes || '';
    },
    async saveJobNotes(job) {
      this.savingJobId = job.id;
      this.editingNotesJobId = null;
      try {
        await setFire({
          collection: 'jobs',
          doc: job.id,
          body: { notes: this.editNotesValue }
        });
        job.notes = this.editNotesValue;
        this.showSaved(job.id);
        this.$emit('job-updated', job.id);
      } catch (e) {
        console.error('Error saving notes:', e);
      } finally {
        this.savingJobId = null;
      }
    },

    // ── Read-Only Mode: Invite Team Leader ──
    openInviteModal(job) {
      this.inviteJobId = job.id;
      this.showInviteModal = true;
    },
    async inviteTeamLeader(tl) {
      const job = this.sortedJobs.find(j => j.id === this.inviteJobId);
      if (!job) return;
      const teamLeaders = job.teamLeaders ? [...job.teamLeaders] : [];
      const teamLeaderIds = job.teamLeaderIds ? [...job.teamLeaderIds] : [];
      if (teamLeaderIds.includes(tl.id)) return;
      teamLeaders.push({ id: tl.id, name: tl.name });
      teamLeaderIds.push(tl.id);
      this.showInviteModal = false;
      this.savingJobId = job.id;
      try {
        await setFire({
          collection: 'jobs',
          doc: job.id,
          body: { teamLeaders, teamLeaderIds }
        });
        job.teamLeaders = teamLeaders;
        job.teamLeaderIds = teamLeaderIds;
        this.showSaved(job.id);
        this.$emit('job-updated', job.id);
        // Create invite notification
        await createTeamLeaderInvite(tl.id, job.id, job);
      } catch (e) {
        console.error('Error inviting team leader:', e);
      } finally {
        this.savingJobId = null;
      }
    },

    // ── Read-Only Mode: Assign Tester ──
    async onAssignTester(event, job) {
      const testerId = event.target.value;
      if (!testerId) return;
      const tester = this.teamMembers.find(m => m.id === testerId);
      if (!tester) return;
      this.assigningTesterJobId = null;
      this.savingJobId = job.id;
      try {
        const contractor = { id: tester.id, name: tester.name };
        await setFire({
          collection: 'jobs',
          doc: job.id,
          body: { contractor }
        });
        job.contractor = contractor;
        this.showSaved(job.id);
        this.$emit('job-updated', job.id);
      } catch (e) {
        console.error('Error assigning tester:', e);
      } finally {
        this.savingJobId = null;
      }
    }
  },
  beforeDestroy() {
    if (this.savedTimer) clearTimeout(this.savedTimer);
  }
};
</script>
